import { QueryRender } from "@/components/helpers/QueryRender"
import { useMemo } from "react"
import { format } from "date-fns"
import { useCurrentSchoolYear } from "@/entities/schoolYear/useSchoolYear"
import { useQuery } from "@tanstack/react-query"
import { Cell, Legend, Pie, PieChart, ResponsiveContainer } from "recharts"
import { fr } from "date-fns/locale"
import { User } from "@/entities/user/user"
import { AttendanceStats } from "@/controller/computeStatsForYoungs"
import { useProcess } from "@/contexts/ProcessContext"
import { computeStatsForYoungs, exportStatsToCSV } from "@/controller/exportStats"
import { chartColors } from "@/config/env"

interface Props {
   period: {
      from: Date
      to: Date
   }
   youngs: User[]
   hidePeriod?: boolean
}
 

export function PieChartAttendance({ period, youngs, hidePeriod }: Props) {
   const { data: currentSchoolYear } = useCurrentSchoolYear()
   const { startProcess } = useProcess()
   const isGroup = youngs.length > 1

   const handleExportToCsv = () => {
         if (!currentSchoolYear) return

         startProcess(async () => {
         await exportStatsToCSV(
            youngs,
            period,
            currentSchoolYear
         )
         })
   }

   const { data: stats, ...query } = useQuery<AttendanceStats | null>({
      queryKey: [
         "collection",
         "attendance_events",
         "statistics",
         youngs
            .map((young) => young.id)
            .sort()
            .join(","),
         currentSchoolYear?.id,
         period.from.toISOString(),
         period.to.toISOString(),
      ],
      queryFn: async () => {
         if (!currentSchoolYear?.id) return null

         const { globalStats } = await computeStatsForYoungs(
            youngs,
            { from: period.from, to: period.to },
            currentSchoolYear
         )

         return globalStats
      },
      enabled: !!currentSchoolYear?.id,
   })

   const chartData = useMemo(() => {
      if (!stats) return []
      return [
         { name: "Prés", value: stats.presentPercentage, color: chartColors.present },
         { name: "Abs Ju", value: stats.absentJustifiedPercentage, color: chartColors.absentJustified },
         { name: "Abs In", value: stats.absentNotJustifiedPercentage, color: chartColors.absentNotJustified },
      ]
   }, [stats])

   return (
      <QueryRender query={query} data={stats}>
         {(stats) => (
            <div className="flex flex-col gap-4">
               {!hidePeriod && (
                  <div className="text-sm text-base-content/60 mx-auto">
                     {format(period.from, "d MMMM", { locale: fr })} →{" "}
                     {format(period.to, "d MMMM", { locale: fr })}
                  </div>
               )}

               <div className="w-full h-64">
                  <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                     <PieChart width={100} height={100}>
                        <Pie
                           data={chartData}
                           cx="50%"
                           cy="50%"
                           innerRadius={60}
                           outerRadius={100}
                           paddingAngle={5}
                           dataKey="value"
                           label
                        >
                           {chartData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                           ))}
                        </Pie>
                        <Legend />
                     </PieChart>
                  </ResponsiveContainer>
               </div>

               <div className="flex flex-col gap-2 mx-auto">
               <span className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                     Présences : {stats.present} jours ({stats.presentPercentage} %)
                  </span>
                  <span className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                     Parfaite présences : {stats.perfectPresent} jours ({stats.perfectPresentPercentage} %)
                  </span>
                  <span className="inline-flex items-center rounded-md bg-yellow-50 px-2 py-1 text font-medium text-yellow-800 ring-1 ring-inset ring-yellow-600/20">
                     Retards : {stats.late} jours ({stats.latePercentage} %)
                  </span>
                  <span className="inline-flex items-center rounded-md bg-red-50 px-2 py-1 text font-medium text-blue-700 ring-1 ring-inset ring-red-600/20">
                     Absences justifiées : {stats.absentJustified} jours ({stats.absentJustifiedPercentage} %)
                  </span>
                  <span className="inline-flex items-center rounded-md bg-red-50 px-2 py-1 text font-medium text-red-700 ring-1 ring-inset ring-red-600/20">
                     Absences injustifiées : {stats.absentNotJustified} jours ({stats.absentNotJustifiedPercentage} %)
                  </span>
               </div>


               {!isGroup && (
                  <div className="text-sm text-base-content/60 mx-auto">
                     Sur {stats.inSchoolDaysCount} jours
                  </div>
               )}

               <button onClick={handleExportToCsv} className="btn btn-sm btn-outline mx-auto">
                  <svg
                     xmlns="http://www.w3.org/2000/svg"
                     viewBox="0 0 24 24"
                     fill="currentColor"
                     className="size-6"
                  >
                     <path
                        fillRule="evenodd"
                        d="M12 2.25a.75.75 0 0 1 .75.75v11.69l3.22-3.22a.75.75 0 1 1 1.06 1.06l-4.5 4.5a.75.75 0 0 1-1.06 0l-4.5-4.5a.75.75 0 1 1 1.06-1.06l3.22 3.22V3a.75.75 0 0 1 .75-.75Zm-9 13.5a.75.75 0 0 1 .75.75v2.25a1.5 1.5 0 0 0 1.5 1.5h13.5a1.5 1.5 0 0 0 1.5-1.5V16.5a.75.75 0 0 1 1.5 0v2.25a3 3 0 0 1-3 3H5.25a3 3 0 0 1-3-3V16.5a.75.75 0 0 1 .75-.75Z"
                        clipRule="evenodd"
                     />
                  </svg>
                  Exporter CSV
               </button>
            </div>
         )}
      </QueryRender>
   )
}
